import { readFileSync } from 'fs';
import { join } from 'path';

import React, { useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import {
  getCurrentPage,
  getFreeConsultationDefaultValues,
  getFreeConsultationPages,
} from '@/components/Questionnaires/FreeConsultation/utils/FreeConsultationUtils';
import { QuestionnairePageOptions } from '@/components/Questionnaires/QuestionnairePage';
import { handleFormSubmit } from '@/utils/handleFormSubmit';
import { Col, Hero } from '@visalex/core';

import { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import commonStyles from '@/components/Questionnaires/Common/QuestionnairesCommon.module.scss';
import styles from '@/components/Questionnaires/FreeConsultation/FreeConsultation.module.scss';

const FreeConsultationFormSection = dynamic(
  import('@/components/Questionnaires/FreeConsultation/FreeConsultationFormSection'),
  { ssr: false }
);

export type FreeConsultationProps = { query: Record<string, string>; countries: Array<string>[] };

const FreeConsultation: NextPage<FreeConsultationProps> = ({ query = {}, countries = [] }) => {
  const { t, i18n } = useTranslation();
  const defaultValues = useMemo(() => getFreeConsultationDefaultValues(query, countries), [i18n.language]);

  const { handleSubmit, isSubmitWithError, isLoading } = handleFormSubmit();

  const pages = useMemo<QuestionnairePageOptions[]>(
    () => getFreeConsultationPages(t, query, countries, isLoading),
    [i18n.language, isLoading]
  );
  const [initialPage] = useState<number>(getCurrentPage(query, pages));

  return (
    <Layout title={t('home.seo-title')} description={t('home.seo-description')}>
      <div className={styles['free-consultation']}>
        <Hero className={commonStyles.questionnaire__hero}>
          <Col xs={12} lg={8} className="mx-auto">
            <h1>{t('free-consultation.title')}</h1>
            <p>{t('free-consultation.subtitle')}</p>
          </Col>
        </Hero>
        <FreeConsultationFormSection
          pages={pages}
          defaultValues={defaultValues}
          initialPage={initialPage}
          handleSubmit={handleSubmit}
          isSubmitWithError={isSubmitWithError}
          isLoading={isLoading}
        />
      </div>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale, query }) => {
  const res = await readFileSync(join(process.cwd(), `./assets/country-list/${locale}.json`));
  const countries = JSON.parse(res.toString());

  return {
    props: { query, countries, ...(await serverSideTranslations(locale as string)) }, // will be passed to the page component as props
  };
};

export default FreeConsultation;
